import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
} from "firebase/firestore";
import { createQuery } from "@tanstack/solid-query";
import { firebase } from "@core/firebase/app";
import { queryKeys } from "@core/query";
import { toDate } from "@modules/shared/firestore";
import type {
  BookingDetail,
  BookingScheduleSummary,
  BookingStatusHistoryEntry,
  BookingSummary,
  BookingUserSummary,
} from "./types";

const BOOKINGS_COLLECTION = "bookings";
const USERS_COLLECTION = "users";

const readString = (value: unknown, fallback: string | null = null): string | null => {
  if (typeof value === "string" && value.trim().length > 0) {
    return value;
  }
  return fallback;
};

const readNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  return null;
};

const mapSchedule = (raw: unknown): BookingScheduleSummary => {
  if (typeof raw !== "object" || raw === null) {
    return {
      scheduledDate: null,
      timeSlotLabel: null,
      buildingId: null,
      floor: null,
      apartmentId: null,
      notes: null,
    };
  }

  const data = raw as Record<string, unknown>;
  return {
    scheduledDate: readString(data["scheduledDate"]),
    timeSlotLabel: readString(data["timeSlotLabel"]),
    buildingId: readString(data["buildingId"]),
    floor: readNumber(data["floor"]),
    apartmentId: readString(data["apartmentId"]),
    notes: readString(data["notes"]),
  };
};

const mapStatusHistory = (raw: unknown): BookingStatusHistoryEntry[] => {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw
    .map((entry) => {
      if (typeof entry !== "object" || entry === null) {
        return null;
      }
      const data = entry as Record<string, unknown>;
      const status = readString(data["status"], "unknown");
      const at = toDate(data["at"] ?? data["timestamp"] ?? data["createdAt"] ?? null);
      const actor = readString(data["actor"]);
      const notes = readString(data["notes"]);
      return {
        status: status ?? "unknown",
        at,
        actor,
        notes,
        raw: data,
      } satisfies BookingStatusHistoryEntry;
    })
    .filter(Boolean) as BookingStatusHistoryEntry[];
};

export const mapBookingSummary = (id: string, raw: Record<string, unknown>): BookingSummary => {
  const schedule = mapSchedule(raw["schedule"]);
  return {
    id,
    userId: readString(raw["userId"]),
    status: readString(raw["status"], "unknown") ?? "unknown",
    serviceId: readString(raw["serviceId"]),
    roomType: readString(raw["roomType"]),
    amount: readNumber(raw["amount"]),
    currency: readString(raw["currency"]),
    paymentIntentId: readString(raw["paymentIntentId"]),
    source: readString(raw["source"]),
    createdAt: toDate(raw["createdAt"]),
    updatedAt: toDate(raw["updatedAt"]),
    schedule,
  };
};

const mapUserSummary = (id: string, raw: Record<string, unknown>): BookingUserSummary => ({
  id,
  displayName: readString(raw["displayName"], readString(raw["email"], readString(raw["phoneNumber"], null))),
  email: readString(raw["email"]),
  phoneNumber: readString(raw["phoneNumber"]),
});

export const fetchRecentBookings = async (take = 40): Promise<BookingSummary[]> => {
  const bookingsRef = collection(firebase.firestore, BOOKINGS_COLLECTION);
  const bookingsQuery = query(bookingsRef, orderBy("createdAt", "desc"), limit(take));
  const snapshot = await getDocs(bookingsQuery);
  return snapshot.docs.map((docSnap) => mapBookingSummary(docSnap.id, docSnap.data() as Record<string, unknown>));
};

const fetchBookingUser = async (userId: string | null): Promise<BookingUserSummary | null> => {
  if (!userId) {
    return null;
  }
  const userRef = doc(firebase.firestore, USERS_COLLECTION, userId);
  const snapshot = await getDoc(userRef);
  if (!snapshot.exists()) {
    return null;
  }
  return mapUserSummary(snapshot.id, snapshot.data() as Record<string, unknown>);
};

export const fetchBookingDetail = async (bookingId: string): Promise<BookingDetail | null> => {
  const bookingRef = doc(firebase.firestore, BOOKINGS_COLLECTION, bookingId);
  const snapshot = await getDoc(bookingRef);
  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data() as Record<string, unknown>;
  const summary = mapBookingSummary(snapshot.id, data);
  const [user] = await Promise.all([fetchBookingUser(summary.userId)]);
  const statusHistory = mapStatusHistory(data["statusHistory"]);

  return {
    summary,
    user,
    statusHistory,
  };
};

export const useBookingListQuery = () =>
  createQuery(() => ({
    queryKey: queryKeys.bookings.root(),
    queryFn: () => fetchRecentBookings(),
  }));

export const useBookingDetailQuery = (bookingId: () => string | null) =>
  createQuery(() => {
    const id = bookingId();
    return {
      queryKey: queryKeys.bookings.detail(id ?? "detail"),
      enabled: Boolean(id),
      queryFn: () => (id ? fetchBookingDetail(id) : Promise.resolve(null)),
    };
  });
